import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { useI18n } from '@/contexts/I18nContext';

interface SignInFormProps {
  onEmailSent?: (message: string) => void;
}

export const SignInForm = ({ onEmailSent }: SignInFormProps) => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { toast } = useToast();
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
  });
  const [touched, setTouched] = useState({
    email: false,
    password: false,
  });
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let pollingCount = 0;
    const maxPolling = 20; // 20 * 200ms = 4 segundos
    const interval = setInterval(() => {
      const email = emailRef.current?.value || '';
      const passwordInput = document.querySelector('input[type="password"], input[type="text"][autocomplete="current-password"]') as HTMLInputElement | null;
      const password = passwordInput?.value || '';
      if (email && password && (formData.email !== email || formData.password !== password)) {
        setFormData({ email, password });
        setTouched({ email: true, password: true });
        clearInterval(interval);
      }
      pollingCount++;
      if (pollingCount > maxPolling) {
        clearInterval(interval);
      }
    }, 200);

    // MutationObserver para detectar mudanças no campo de senha
    const passwordInput = document.querySelector('input[type="password"], input[type="text"][autocomplete="current-password"]') as HTMLInputElement | null;
    let observer: MutationObserver | null = null;
    if (passwordInput) {
      observer = new MutationObserver(() => {
        const email = emailRef.current?.value || '';
        const password = passwordInput.value || '';
        if (email && password && (formData.email !== email || formData.password !== password)) {
          setFormData({ email, password });
          setTouched({ email: true, password: true });
        }
      });
      observer.observe(passwordInput, { attributes: true, attributeFilter: ['value'] });
    }

    return () => {
      clearInterval(interval);
      if (observer) observer.disconnect();
    };
  }, []);

  // Função para detectar autofill via animation
  const handleAutoFill = () => {
    const email = emailRef.current?.value || '';
    const passwordInput = document.querySelector('input[type="password"], input[type="text"][autocomplete="current-password"]') as HTMLInputElement | null;
    const password = passwordInput?.value || '';
    if (email && password && (formData.email !== email || formData.password !== password)) {
      setFormData({ email, password });
      setTouched({ email: true, password: true });
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const isLongEnough = password.length >= 8;

    return hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && isLongEnough;
  };

  const isFormValid = () => {
    return (
      formData.email &&
      formData.password &&
      validateEmail(formData.email) &&
      validatePassword(formData.password) &&
      !errors.email &&
      !errors.password
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await signIn(formData.email, formData.password);
      
      if (error) {
        // AuthContext already shows specific error toasts, so we don't need to show them here
        // Just handle navigation on success
        console.log('Sign in error:', error);
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Unexpected error in sign in form:', error);
      // Only show toast for unexpected errors that weren't handled by AuthContext
      toast({
        title: t('auth.error'),
        description: t('auth.unexpected_error'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailBlur = () => {
    setTouched(prev => ({ ...prev, email: true }));
    if (!formData.email) {
      setErrors(prev => ({ ...prev, email: t('auth.sign_in_form.email_required') }));
    } else if (!validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: t('auth.sign_in_form.email_invalid') }));
    } else {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePasswordBlur = () => {
    setTouched(prev => ({ ...prev, password: true }));
    if (!formData.password) {
      setErrors(prev => ({ 
        ...prev, 
        password: t('auth.sign_in_form.password_validation')
      }));
    } else if (!validatePassword(formData.password)) {
      setErrors(prev => ({ 
        ...prev, 
        password: t('auth.sign_in_form.password_validation')
      }));
    } else {
      setErrors(prev => ({ ...prev, password: '' }));
    }
  };

  if (showForgotPassword) {
    return <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8 bg-[#160C0C] py-8 px-4 rounded-2xl shadow-xl">
      <div className="relative">
      <Input
        ref={emailRef}
        autoComplete="email"
        type="email"
        placeholder={t('auth.sign_in_form.email_placeholder')}
        value={formData.email}
        onChange={(e) => handleInputChange('email', e.target.value)}
        onBlur={handleEmailBlur}
        className={errors.email && touched.email ? 'border-red-500' : ''}
        required
        onAnimationStart={handleAutoFill}
      />
        {errors.email && touched.email && (
          <p className="mt-1 text-sm text-white/80">{errors.email}</p>
        )}
      </div>

      <div className="relative">
        <PasswordInput
          autoComplete="current-password"
          placeholder={t('auth.sign_in_form.password_placeholder')}
          value={formData.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          onBlur={handlePasswordBlur}
          error={!!errors.password && touched.password}
          label={t('auth.sign_in_form.password_label')}
          required
          onAnimationStart={handleAutoFill}
        />
        {errors.password && touched.password && (
          <p className="mt-1 text-sm text-white/80">{errors.password}</p>
        )}
      </div>

      <div className="flex items-center space-x-4">
        <Button
          type="submit"
          disabled={loading || !isFormValid()}
          className="flex-1 bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-4 h-14 rounded-full transition-colors duration-200 disabled:opacity-50"
        >
          {loading ? t('auth.sign_in_form.signing_in') : t('auth.sign_in_form.sign_in_button')}
        </Button>
        <button
          type="button"
          onClick={() => setShowForgotPassword(true)}
          className="text-white/80 hover:text-white text-sm underline whitespace-nowrap"
        >
          {t('auth.sign_in_form.forgot_password')}
        </button>
      </div>
    </form>
  );
};
